import React, { useCallback, useMemo, useState } from 'react';
import InputNumberFormat from 'react-number-format';

import {
  ArrowDownIcon,
  ArrowUpIcon,
  EditIcon,
  RepeatIcon,
} from '@chakra-ui/icons';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  Grid,
  HStack,
  Icon,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
} from '@chakra-ui/react';

import { isPositiveCurrency } from './validationUtils';

type BulkUpdatePanelProps = {
  selectedCount: number;
  onApplyFixedPrice: (value: number | null | undefined) => void;
  onApplyPercentage: (percentage: number | null | undefined) => void;
  onClearSelection: () => void;
  onUndoSelectedChanges: () => void;
};

const BulkUpdatePanel: React.FC<BulkUpdatePanelProps> = ({
  selectedCount,
  onApplyFixedPrice,
  onApplyPercentage,
  onClearSelection,
  onUndoSelectedChanges,
}) => {
  const [fixedPriceValue, setFixedPriceValue] = useState<number | null>(null);
  const [percentageValue, setPercentageValue] = useState<number | null>(null);
  const [bulkUpdateType, setBulkUpdateType] = useState<'fixed' | 'percentage'>(
    'fixed',
  );
  const [percentageDirection, setPercentageDirection] = useState<
    'increase' | 'decrease'
  >('increase');

  const canApplyFixedPrice = useMemo(() => {
    return selectedCount > 0 && isPositiveCurrency(fixedPriceValue);
  }, [fixedPriceValue, selectedCount]);

  const canApplyPercentage = useMemo(() => {
    return selectedCount > 0 && isPositiveCurrency(percentageValue);
  }, [percentageValue, selectedCount]);

  const canApplyCurrentUpdate = useMemo(() => {
    if (bulkUpdateType === 'fixed') {
      return canApplyFixedPrice;
    }

    return canApplyPercentage;
  }, [bulkUpdateType, canApplyFixedPrice, canApplyPercentage]);

  const isPercentageBulkUpdate = bulkUpdateType === 'percentage';

  const handleApplyCurrentUpdate = useCallback(() => {
    if (!canApplyCurrentUpdate) {
      return;
    }

    if (bulkUpdateType === 'fixed') {
      onApplyFixedPrice(fixedPriceValue);
      return;
    }

    let normalizedPercentage: number | null = null;

    if (typeof percentageValue === 'number') {
      if (percentageDirection === 'increase') {
        normalizedPercentage = percentageValue;
      } else {
        normalizedPercentage = -percentageValue;
      }
    }

    onApplyPercentage(normalizedPercentage);
  }, [
    bulkUpdateType,
    canApplyCurrentUpdate,
    fixedPriceValue,
    onApplyFixedPrice,
    onApplyPercentage,
    percentageDirection,
    percentageValue,
  ]);

  const handleApplyOnEnter = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();
      handleApplyCurrentUpdate();
    },
    [handleApplyCurrentUpdate],
  );

  const handleClearSelection = useCallback(() => {
    setFixedPriceValue(null);
    setPercentageValue(null);
    onClearSelection();
  }, [onClearSelection]);

  return (
    <Flex
      p={3}
      mb={3}
      bg="gray.50"
      borderWidth="1px"
      borderRadius="md"
      borderColor="gray.100"
      flexDirection="column"
      gridGap={3}
      data-testid="bulk-edit-selected-actions"
    >
      <Flex direction="column" align="stretch" gap={3}>
        <Flex alignItems="center" minW={0} flex="1 1 auto">
          <HStack spacing={3} alignItems="center">
            <Flex
              width="40px"
              height="40px"
              alignItems="center"
              justifyContent="center"
              borderRadius="md"
              bg="blue.500"
              color="white"
              flexShrink={0}
            >
              <Icon as={EditIcon} boxSize={4} />
            </Flex>

            <Box minW={0}>
              <Text
                fontSize={{ base: 'xl', md: '2xl' }}
                fontWeight="bold"
                color="gray.800"
                lineHeight="1.1"
              >
                {selectedCount} itens selecionados
              </Text>
              <Text fontSize="xs" color="gray.600" mt={1}>
                Altere os valores dos itens selecionados em massa.
              </Text>
            </Box>
          </HStack>
        </Flex>

        <Box minW={0} flex="1 1 auto">
          <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={3}>
            <Box minW={0}>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                Tipo de alteração
              </Text>

              <RadioGroup
                colorScheme="blue"
                value={bulkUpdateType}
                onChange={(value) => {
                  setBulkUpdateType(value as 'fixed' | 'percentage');
                }}
              >
                <HStack
                  spacing={4}
                  width="100%"
                  flexDirection={{ base: 'column', sm: 'row' }}
                  alignItems={{ base: 'flex-start', sm: 'center' }}
                >
                  <Radio value="fixed" size="md">
                    Valor unitário
                  </Radio>

                  <Radio value="percentage" size="md">
                    Ajuste percentual
                  </Radio>
                </HStack>
              </RadioGroup>
            </Box>

            <Box minW={0}>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                {isPercentageBulkUpdate
                  ? 'Ajuste percentual'
                  : 'Valor unitário'}
              </Text>

              {isPercentageBulkUpdate ? (
                <Stack spacing={2}>
                  <HStack
                    spacing={2}
                    alignItems="stretch"
                    flexDirection={{ base: 'column', sm: 'row' }}
                  >
                    <ButtonGroup
                      isAttached
                      size="sm"
                      variant="outline"
                      width={{ base: '100%', sm: 'auto' }}
                    >
                      <Button
                        colorScheme={
                          percentageDirection === 'increase' ? 'blue' : 'gray'
                        }
                        variant={
                          percentageDirection === 'increase'
                            ? 'solid'
                            : 'outline'
                        }
                        onClick={() => setPercentageDirection('increase')}
                        leftIcon={<ArrowUpIcon />}
                      >
                        Acréscimo
                      </Button>

                      <Button
                        colorScheme={
                          percentageDirection === 'decrease' ? 'blue' : 'gray'
                        }
                        variant={
                          percentageDirection === 'decrease'
                            ? 'solid'
                            : 'outline'
                        }
                        onClick={() => setPercentageDirection('decrease')}
                        leftIcon={<ArrowDownIcon />}
                      >
                        Desconto
                      </Button>
                    </ButtonGroup>

                    <Box flex={1}>
                      <InputNumberFormat
                        type="tel"
                        displayType="input"
                        thousandSeparator="."
                        decimalSeparator=","
                        allowNegative={false}
                        decimalScale={2}
                        fixedDecimalScale
                        // @ts-expect-error size prop does not exist on type
                        size="sm"
                        suffix=" %"
                        customInput={Input}
                        value={percentageValue ?? ''}
                        onValueChange={(values) => {
                          if (typeof values.floatValue === 'number') {
                            setPercentageValue(values.floatValue);
                            return;
                          }

                          setPercentageValue(null);
                        }}
                        onKeyDown={handleApplyOnEnter}
                        placeholder="0,00 %"
                        aria-label="Variação percentual para seleção"
                        style={{ textAlign: 'left' }}
                      />
                    </Box>
                  </HStack>
                </Stack>
              ) : (
                <InputNumberFormat
                  type="tel"
                  displayType="input"
                  thousandSeparator="."
                  decimalSeparator=","
                  allowNegative={false}
                  decimalScale={2}
                  fixedDecimalScale
                  // @ts-expect-error size prop does not exist on type
                  size="sm"
                  prefix="R$ "
                  customInput={Input}
                  value={fixedPriceValue ?? ''}
                  onValueChange={(values) => {
                    setFixedPriceValue(values.floatValue || null);
                  }}
                  onKeyDown={handleApplyOnEnter}
                  placeholder="R$ 0,00"
                  aria-label="Definir valor unitário para seleção"
                  style={{ textAlign: 'left' }}
                />
              )}
            </Box>
          </Grid>
        </Box>

        <Flex
          width="100%"
          flex="0 0 auto"
          justifyContent="stretch"
          alignItems="stretch"
        >
          <Button
            size="sm"
            width="100%"
            colorScheme="blue"
            variant="outline"
            onClick={handleApplyCurrentUpdate}
            isDisabled={!canApplyCurrentUpdate}
            fontSize="sm"
            fontWeight="bold"
          >
            Aplicar aos {selectedCount} itens
          </Button>
        </Flex>
      </Flex>

      <Divider borderColor="gray.200" />

      <HStack
        spacing={3}
        width={{ base: '100%', md: 'auto' }}
        justifyContent={{ base: 'space-between', md: 'flex-start' }}
        flexWrap="wrap"
      >
        <Button
          size="sm"
          variant="outline"
          colorScheme="blackAlpha"
          onClick={handleClearSelection}
          width={{ base: '100%', sm: 'auto' }}
          px={4}
        >
          Limpar seleção
        </Button>

        <Button
          size="sm"
          variant="outline"
          colorScheme="blackAlpha"
          onClick={onUndoSelectedChanges}
          width={{ base: '100%', sm: 'auto' }}
          px={4}
          leftIcon={<RepeatIcon />}
        >
          Desfazer alterações
        </Button>
      </HStack>
    </Flex>
  );
};

export default BulkUpdatePanel;
